import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { appsAPI, appSystemSettingsAPI } from '../../services/api'

import { logger } from '@/utils/logger';

const EMPTY_FORM = {
  host: '',
  port: '587',
  username: '',
  password: '',
  security: 'STARTTLS',
  from_name: '',
  from_email: '',
  reply_to: '',
  bcc: '',
  company_name: '',
  contact_phone: '',
  contact_email: '',
  logo_url: '',
  test_connectivity: false,
}

export default function AppSystemSettings() {
  const { appId } = useParams()
  const navigate = useNavigate()

  const [app, setApp] = useState(null)
  const [passwordSet, setPasswordSet] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [formData, setFormData] = useState(EMPTY_FORM)

  useEffect(() => {
    loadData()
  }, [appId])

  const loadData = async () => {
    try {
      const appRes = await appsAPI.getById(appId)
      setApp(appRes.data)

      try {
        const settingsRes = await appSystemSettingsAPI.get(appId)
        const s = settingsRes.data
        setFormData({
          ...EMPTY_FORM,
          host: s.host || '',
          port: String(s.port || '587'),
          username: s.username || '',
          security: s.security || 'STARTTLS',
          from_name: s.from_name || '',
          from_email: s.from_email || '',
          reply_to: s.reply_to || '',
          bcc: s.bcc || '',
          company_name: s.company_name || '',
          contact_phone: s.contact_phone || '',
          contact_email: s.contact_email || '',
          logo_url: s.logo_url || '',
        })
        setPasswordSet(!!s.password_set)
      } catch (err) {
        if (err.response?.status !== 404) throw err
        // Noch keine Einstellungen hinterlegt — leeres Formular ist ok
      }
    } catch (err) {
      setError('Fehler beim Laden der Daten')
      logger.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setSaving(true)

    try {
      const submitData = {
        ...formData,
        port: parseInt(formData.port, 10),
        reply_to: formData.reply_to || null,
        bcc: formData.bcc || null,
        company_name: formData.company_name || null,
        contact_phone: formData.contact_phone || null,
        contact_email: formData.contact_email || null,
        logo_url: formData.logo_url || null,
      }
      if (!submitData.password) delete submitData.password

      const res = await appSystemSettingsAPI.update(appId, submitData)
      setPasswordSet(!!res.data.password_set)
      setFormData((prev) => ({ ...prev, password: '', test_connectivity: false }))
      setSuccess('System-Einstellungen gespeichert und an die App übertragen')
      setTimeout(() => setSuccess(''), 4000)
    } catch (err) {
      const detail = err.response?.data?.detail
      setError(
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => (typeof d === 'object' ? d.msg || JSON.stringify(d) : d)).join('; ')
            : 'Fehler beim Speichern'
      )
      logger.error(err)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <div className="wc-spinner" />
      </div>
    )
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <button
          onClick={() => navigate('/admin/apps')}
          className="wc-btn wc-btn-ghost"
          style={{ padding: 0, marginBottom: 8 }}
        >
          ← Zurück zu Apps
        </button>
        <h1 style={{ fontSize: 24, margin: 0 }}>System-Einstellungen für {app?.name}</h1>
        <p className="wc-muted" style={{ fontSize: 14, margin: '4px 0 0' }}>
          Globale SMTP-Einstellungen der App (z.B. Passwort-Reset-Mails). Änderungen werden per
          Webhook direkt an die App übertragen.
        </p>
      </div>

      {error && <div className="wc-alert wc-alert-error" style={{ marginBottom: 16 }}>{error}</div>}
      {success && <div className="wc-alert wc-alert-success" style={{ marginBottom: 16 }}>{success}</div>}

      <form onSubmit={handleSubmit} className="wc-card wc-elev-sm" style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 720 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          <div className="wc-field">
            <label>SMTP Host *</label>
            <input
              type="text"
              value={formData.host}
              onChange={(e) => setFormData({ ...formData, host: e.target.value })}
              className="wc-input"
              placeholder="smtp.example.com"
              required
            />
          </div>
          <div className="wc-field">
            <label>Port *</label>
            <input
              type="number"
              value={formData.port}
              onChange={(e) => setFormData({ ...formData, port: e.target.value })}
              className="wc-input"
              placeholder="587"
              required
            />
          </div>
          <div className="wc-field">
            <label>Sicherheit *</label>
            <select
              value={formData.security}
              onChange={(e) => setFormData({ ...formData, security: e.target.value })}
              className="wc-input"
            >
              <option value="NONE">Keine</option>
              <option value="STARTTLS">STARTTLS</option>
              <option value="SSL">SSL/TLS</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
          <div className="wc-field">
            <label>Benutzername *</label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              className="wc-input"
              placeholder="user@example.com"
              required
            />
          </div>
          <div className="wc-field">
            <label>Passwort</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="wc-input"
              placeholder={passwordSet ? 'Neues Passwort eingeben (optional)' : 'Passwort eingeben'}
            />
            <p className="wc-muted" style={{ fontSize: 11, margin: '6px 0 0' }}>
              {passwordSet ? 'Ein Passwort ist in der App bereits hinterlegt. Wird nur geändert, wenn hier ein neues eingegeben wird.' : 'Wird direkt an die App übertragen, Portal speichert es nicht.'}
            </p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
          <div className="wc-field">
            <label>Absender-Name *</label>
            <input
              type="text"
              value={formData.from_name}
              onChange={(e) => setFormData({ ...formData, from_name: e.target.value })}
              className="wc-input"
              required
            />
          </div>
          <div className="wc-field">
            <label>Absender-E-Mail *</label>
            <input
              type="email"
              value={formData.from_email}
              onChange={(e) => setFormData({ ...formData, from_email: e.target.value })}
              className="wc-input"
              required
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
          <div className="wc-field">
            <label>Reply-To (optional)</label>
            <input
              type="email"
              value={formData.reply_to}
              onChange={(e) => setFormData({ ...formData, reply_to: e.target.value })}
              className="wc-input"
            />
          </div>
          <div className="wc-field">
            <label>BCC (optional, durch Komma getrennt)</label>
            <input
              type="text"
              value={formData.bcc}
              onChange={(e) => setFormData({ ...formData, bcc: e.target.value })}
              className="wc-input"
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          <div className="wc-field">
            <label>Firmenname (optional)</label>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              className="wc-input"
            />
          </div>
          <div className="wc-field">
            <label>Kontakt-Telefon (optional)</label>
            <input
              type="text"
              value={formData.contact_phone}
              onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
              className="wc-input"
            />
          </div>
          <div className="wc-field">
            <label>Kontakt-E-Mail (optional)</label>
            <input
              type="email"
              value={formData.contact_email}
              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              className="wc-input"
            />
          </div>
        </div>

        <div className="wc-field">
          <label>Logo-URL (optional)</label>
          <input
            type="text"
            value={formData.logo_url}
            onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
            className="wc-input"
          />
        </div>

        <label className="wc-check">
          <input
            type="checkbox"
            checked={formData.test_connectivity}
            onChange={(e) => setFormData({ ...formData, test_connectivity: e.target.checked })}
          />
          Verbindung beim Speichern testen
        </label>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <button type="submit" className="wc-btn wc-btn-primary" disabled={saving}>
            {saving ? 'Speichern…' : 'Speichern'}
          </button>
        </div>
      </form>
    </div>
  )
}
